/**
 * Command registry: everything the command palette lists and tinykeys binds.
 * Commands are registered at module scope (appCommands.ts) and can be added
 * by features at any time. Shortcuts are user-remappable via overrides
 * persisted in localStorage (settings UI in the polish phase).
 */

export interface AppCommand {
  id: string;
  title: string;
  /** tinykeys syntax with $mod, e.g. "$mod+p". Display derives from this. */
  defaultKeys?: string;
  /** group label in the palette */
  section: string;
  run: () => void;
  /** hidden commands are bindable but not listed */
  hidden?: boolean;
}

const commands = new Map<string, AppCommand>();
const listeners = new Set<() => void>();

export function registerCommand(cmd: AppCommand) {
  commands.set(cmd.id, cmd);
  for (const l of listeners) l();
}

export function allCommands(): AppCommand[] {
  return [...commands.values()];
}

export function runCommand(id: string) {
  commands.get(id)?.run();
}

export function subscribeCommands(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// ---------- shortcut overrides ----------

const OVERRIDES_KEY = "hn.shortcuts";

export function shortcutOverrides(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(OVERRIDES_KEY) ?? "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

export function setShortcutOverride(commandId: string, keys: string | null) {
  const o = shortcutOverrides();
  if (keys === null) delete o[commandId];
  else o[commandId] = keys;
  localStorage.setItem(OVERRIDES_KEY, JSON.stringify(o));
  for (const l of listeners) l();
}

export function effectiveKeys(cmd: AppCommand): string | undefined {
  return shortcutOverrides()[cmd.id] ?? cmd.defaultKeys;
}

/** "$mod+shift+p" -> "Mod+Shift+P" for <Kbd> display. */
export function displayKeys(keys: string): string {
  return keys
    .split("+")
    .map((k) => (k === "$mod" ? "Mod" : k.length === 1 ? k.toUpperCase() : k[0]!.toUpperCase() + k.slice(1)))
    .join("+");
}
